import { Events } from './events';
import { localize } from './ui/localization';

type ConsoleLevel = 'warn' | 'error';

type ConsoleEntry = {
    level: ConsoleLevel;
    message: string;
    time: number;
};

// Enough history to diagnose a session, without letting a warning storm - a failing asset
// retried every frame - grow memory without bound.
const MAX_CONSOLE_ENTRIES = 500;

const formatArg = (arg: unknown): string => {
    if (arg instanceof Error) {
        return arg.message;
    }
    if (typeof arg === 'string') {
        return arg;
    }
    try {
        const json = JSON.stringify(arg);
        return json === undefined ? String(arg) : json;
    } catch {
        // circular structures and other values JSON cannot represent
        return String(arg);
    }
};

// Surfaces the warnings and errors the app already reports - loader failures, import and
// export problems, uncaught exceptions - in the editor's Console tab. It observes rather than
// replaces: console.warn and console.error still reach the browser's devtools unchanged.
const registerConsoleLog = (events: Events) => {
    const entries: ConsoleEntry[] = [];

    // a console.entry listener that itself warns must not recurse back in here
    let recording = false;

    const record = (level: ConsoleLevel, message: string) => {
        if (recording) {
            return;
        }

        recording = true;
        try {
            const entry: ConsoleEntry = { level, message, time: Date.now() };
            entries.push(entry);
            if (entries.length > MAX_CONSOLE_ENTRIES) {
                entries.shift();
            }
            events.fire('console.entry', entry);
        } finally {
            recording = false;
        }
    };

    (['warn', 'error'] as ConsoleLevel[]).forEach((level) => {
        const original = console[level].bind(console);
        console[level] = (...args: unknown[]) => {
            original(...args);
            record(level, args.map(formatArg).join(' '));
        };
    });

    window.addEventListener('error', (event: ErrorEvent) => {
        record('error', event.message);
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        record('error', formatArg(event.reason));
    });

    events.function('console.entries', () => entries.slice());

    events.on('console.clear', () => {
        entries.length = 0;
        events.fire('console.cleared');
    });
};

// Context loss blanks the viewport with no message of its own, so report it where the user
// can see it.
const watchWebglContext = (canvas: HTMLCanvasElement) => {
    canvas.addEventListener('webglcontextlost', () => {
        console.error(localize('console.webgl-context-lost'));
    });
};

export { registerConsoleLog, watchWebglContext, MAX_CONSOLE_ENTRIES };
export type { ConsoleEntry, ConsoleLevel };
